import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import { getTheme, getVars } from '../../global/utils';

const ButtonBasic = styled.button`
  cursor: pointer;
  background-color: ${getTheme('bg', 'primary')};
  border: 0;
  && {
    color: ${getTheme('textColor', 'white')};
  }
  text-transform: uppercase;
  font-weight: 700;
  text-align: center;
  line-height: 1;
  text-decoration: none;
  margin-bottom: ${getTheme('marginBottom', 'l')};
  border-radius: ${props =>
    (props.size === 'xsmall' && getTheme('radius', 'xs')) ||
    (props.size === 'small' && getTheme('radius', 's')) ||
    (props.size === 'medium' && getTheme('radius', 'm')) ||
    (props.size === 'large' && getTheme('radius', 'l')) ||
    getTheme('radius', 'm')};
  font-size: ${props =>
    (props.size === 'xsmall' && getTheme('fontSize', 'caption')) ||
    (props.size === 'small' && getTheme('fontSize', 'note')) ||
    (props.size === 'medium' && getTheme('fontSize', 'body')) ||
    (props.size === 'large' && getTheme('fontSize', 'title')) ||
    getTheme('fontSize', 'body')};
  padding: ${props =>
      (props.size === 'xsmall' && getTheme('padding', 'verticalXS')) ||
      (props.size === 'small' && getTheme('padding', 'verticalS')) ||
      (props.size === 'medium' && getTheme('padding', 'verticalM')) ||
      (props.size === 'large' && getTheme('padding', 'verticalL')) ||
      getTheme('padding', 'verticalM')}
    ${props =>
      (props.size === 'xsmall' && getTheme('padding', 'horizontalXS')) ||
      (props.size === 'small' && getTheme('padding', 'horizontalS')) ||
      (props.size === 'medium' && getTheme('padding', 'horizontalM')) ||
      (props.size === 'large' && getTheme('padding', 'horizontalL')) ||
      getTheme('padding', 'horizontalM')};
  min-height: ${props =>
    (props.size === 'xsmall' && getTheme('height', 'xs')) ||
    (props.size === 'small' && getTheme('height', 's')) ||
    (props.size === 'medium' && getTheme('height', 'm')) ||
    (props.size === 'large' && getTheme('height', 'l')) ||
    getTheme('height', 'm')};

  &&:hover {
    background-color: ${getTheme('primary', '_300')};
    color: ${getTheme('textColor', 'white')};
  }

  &&:active {
    background-color: ${getTheme('primary', '_200')};
    color: ${getTheme('textColor', 'white')};
    box-shadow: none !important;
    outline: 0;
  }

  &&:focus {
    outline: 0;
    box-shadow: 0 0 0 4px ${rgba(getVars('bg', 'active'), 0.5)};
  }

  &&:disabled {
    pointer-events: none;
    opacity: 0.25;
  }

  .themeDark && {
    background-color: ${getTheme('bg', 'primaryLight')};
    color: ${getTheme('textColor', 'black')};
    font-weight: 700;
    &:hover {
      background-color: ${getTheme('primary', '_700')};
      color: ${getTheme('textColor', 'black')};
    }
    &:active {
      background-color: ${getTheme('primary', '_800')};
      color: ${getTheme('textColor', 'black')};
    }
    &:focus {
      box-shadow: 0 0 0 4px ${rgba(getVars('bg', 'active'), 0.25)};
    }
  }

  ${props =>
    props.pill &&
    css`
      border-radius: ${getTheme('radius', 'pill')};
      padding: ${props =>
          (props.size === 'xsmall' && getTheme('padding', 'verticalXS')) ||
          (props.size === 'small' && getTheme('padding', 'verticalS')) ||
          (props.size === 'medium' && getTheme('padding', 'verticalM')) ||
          (props.size === 'large' && getTheme('padding', 'verticalL')) ||
          getTheme('padding', 'verticalM')}
        ${props =>
          (props.size === 'xsmall' && getTheme('padding', 'horizontalXSpill')) ||
          (props.size === 'small' && getTheme('padding', 'horizontalSpill')) ||
          (props.size === 'medium' && getTheme('padding', 'horizontalMpill')) ||
          (props.size === 'large' && getTheme('padding', 'horizontalLpill')) ||
          getTheme('padding', 'horizontalMpill')};
    `};

  ${props =>
    props.pill &&
    props.iconOnly &&
    css`
      padding: ${props =>
        (props.size === 'xsmall' && getTheme('padding', 'verticalXS')) ||
        (props.size === 'small' && getTheme('padding', 'verticalS')) ||
        (props.size === 'medium' && getTheme('padding', 'verticalM')) ||
        (props.size === 'large' && getTheme('padding', 'verticalL')) ||
        getTheme('padding', 'verticalM')};
      min-width: ${props =>
        (props.size === 'xsmall' && getTheme('height', 'xs')) ||
        (props.size === 'small' && getTheme('height', 's')) ||
        (props.size === 'medium' && getTheme('height', 'm')) ||
        (props.size === 'large' && getTheme('height', 'l')) ||
        getTheme('height', 'm')};
    `};

  ${props =>
    props.secondary &&
    css`
      && {
        background-color: transparent;
        border: 2px solid ${getTheme('black', '_800')};
        color: ${getTheme('textColor', 'base')};
      }

      &&:hover {
        border-color: ${getTheme('black', '_600')};
        background-color: transparent;
        color: ${getTheme('textColor', 'base')};
      }
      &&:active {
        border-color: ${getTheme('black', '_400')};
        color: ${getTheme('textColor', 'base')};
      }

      .themeDark && {
        background-color: transparent;
        border-color: ${getTheme('black', '_200')};
        color: ${getTheme('textColor', 'baseLight')};
        font-weight: 700;
        &:hover {
          background-color: transparent;
          border-color: ${getTheme('black', '_400')};
          color: ${getTheme('textColor', 'baseLight')};
        }
        &:active {
          border-color: ${getTheme('black', '_600')};
          color: ${getTheme('textColor', 'baseLight')};
        }
        &:focus {
          box-shadow: 0 0 0 4px ${rgba(getVars('bg', 'active'), 0.25)};
        }
      }
    `};

  ${props =>
    props.secondaryWarning &&
    css`
      && {
        background-color: transparent;
        border: 2px solid ${getTheme('warning', '_700')};
        color: ${getTheme('primary', '_400')};
      }

      &&:hover {
        border-color: ${getTheme('warning', '_500')};
        background-color: transparent;
        color: ${getTheme('primary', '_400')};
      }
      &&:active {
        border-color: ${getTheme('warning', '_300')};
        color: ${getTheme('primary', '_400')};
      }
    `};

  ${props =>
    props.iconBefore &&
    css`
      svg {
        margin-right: ${getTheme('spacer', 's')};
      }
    `};
  ${props =>
    props.iconAfter &&
    css`
      svg {
        margin-left: ${getTheme('spacer', 's')};
      }
    `};
  ${props =>
    props.iconOnly &&
    css`
      font-size: ${props =>
        (props.size === 'xsmall' && getTheme('fontSize', 'body')) ||
        (props.size === 'small' && getTheme('fontSize', 'subheader')) ||
        (props.size === 'medium' && getTheme('fontSize', 'title')) ||
        (props.size === 'large' && getTheme('fontSize', 'headline')) ||
        getTheme('fontSize', 'title')};
    `};
  ${props =>
    props.noMarginBottom &&
    css`
      margin-bottom: 0;
    `};
`;

const ButtonLink = ButtonBasic.withComponent('a');

class Button extends Component {
  render() {
    let RenderedButton = ButtonBasic;

    if (this.props.isLink) {
      RenderedButton = ButtonLink;
    }

    return (
      <RenderedButton
        className={this.props.className}
        secondary={this.props.secondary}
        secondaryWarning={this.props.secondaryWarning}
        size={this.props.size}
        disabled={this.props.disabled}
        pill={this.props.pill}
        href={this.props.href}
        iconBefore={this.props.iconBefore}
        iconAfter={this.props.iconAfter}
        iconOnly={this.props.iconOnly}
        noMarginBottom={this.props.noMarginBottom}
        onClick={this.props.onClick}
      >
        {this.props.iconOnly}
        {this.props.iconBefore}
        {this.props.children}
        {this.props.iconAfter}
      </RenderedButton>
    );
  }
}

export default Button;



// WEBPACK FOOTER //
// ./src/components/buttons/Button.js